import { z } from 'zod';

export const CODE_SKILL_SELECTION_LIMIT = 12;

// Runtime IDs are names, not file paths. Content is resolved inside OpenCode.
const skillId = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[\p{L}\p{N}_][\p{L}\p{N}_.: -]*$/u);
export const codeSkillSelectionSchema = z
  .object({
    id: skillId,
    mention: z
      .object({
        start: z.number().int().min(0).max(100_000),
        end: z.number().int().min(1).max(100_000),
        text: z.string().min(2).max(201),
      })
      .strict()
      .optional(),
  })
  .strict();
export const codeSkillSelectionsSchema = z
  .array(codeSkillSelectionSchema)
  .max(CODE_SKILL_SELECTION_LIMIT);
export type CodeSkillSelection = z.infer<typeof codeSkillSelectionSchema>;

export const codeSkillMetadataSchema = z.object({
  id: skillId,
  name: z.string().min(1).max(500),
  description: z.string().optional(),
  slash: z.boolean().optional(),
  autoinvoke: z.boolean().optional(),
});
export type CodeSkillMetadata = z.infer<typeof codeSkillMetadataSchema>;
export type CodeSkillInventory =
  | { status: 'not-ready'; skills: [] }
  | { status: 'ready'; skills: CodeSkillMetadata[] };

export function codeSkillsOf(entries: readonly unknown[]): CodeSkillMetadata[] {
  // A runtime-valid basename can be unsupported by our selection schema.
  // Keep the rest of the inventory usable without exposing invalid entries.
  const skills = entries.flatMap((entry) => {
    const parsed = codeSkillMetadataSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
  return skills.map(({ description, ...rest }) => ({
    ...rest,
    ...(description === undefined ? {} : { description: description.slice(0, 300) }),
  }));
}

export function normalizeCodeSkills(value: unknown, text: string): CodeSkillSelection[] {
  const selections = codeSkillSelectionsSchema.parse(value ?? []);
  const unique = new Map<string, CodeSkillSelection>();
  for (const selection of selections) {
    const mention = selection.mention;
    if (
      mention &&
      (mention.start >= mention.end ||
        text.slice(mention.start, mention.end) !== mention.text ||
        !['/', '$'].includes(mention.text[0] ?? ''))
    ) {
      throw new Error('Selected skill no longer matches the prompt');
    }
    if (!unique.has(selection.id)) {
      unique.set(selection.id, selection);
    }
  }
  return [...unique.values()];
}
